import { useMyCompany, useMyPermissions } from "@/hooks/queries";
import type { PermissionCategoryKey } from "@/services/gpsdrive-user-permissions.service";

// Único ponto de enforcement de permissão no FRONTEND (não existia
// nenhum até 2026-08-27 — todo enforcement era só backend-side). Fail
// closed: sem dado ainda carregado (loading/erro), nunca libera nada —
// mesmo critério de "linha ausente = false" usado no backend
// (permission-catalog.constants.ts). "administrador" (meta-permissão)
// sempre bypassa, espelhando isAdmin() no backend.
export function usePermission(category: PermissionCategoryKey, action?: string): boolean {
  const { data } = useMyPermissions();
  const blockingEnabled = useBlockingEnabled();
  if (!data) return false;
  if (category === "devices" && (action === "lock" || action === "unlock") && !blockingEnabled) return false;

  const admin = data.find((c) => c.category === "administrador");
  if (admin?.enabled) return true;

  const cat = data.find((c) => c.category === category);
  if (!cat?.enabled) return false;
  if (!action) return true;

  return cat.actions.find((a) => a.action === action)?.enabled ?? false;
}

// Módulo "bloqueio" (Super Master, 2026-11-01): desligado, some bloquear e
// desbloquear até para o admin da empresa. Default do backend é ligado.
export function useBlockingEnabled(): boolean {
  const { data: company } = useMyCompany();
  return company?.modules.bloqueio ?? true;
}

// Usado pra gates "mostra a seção se o usuário tem QUALQUER uma destas
// ações" — ex.: o item "Administração" na sidebar aparece se o usuário
// tem acesso a pelo menos uma das 4 seções do menu, mesmo sem ter todas.
export function useAnyPermission(category: PermissionCategoryKey, actions: string[]): boolean {
  const { data } = useMyPermissions();
  if (!data) return false;

  const admin = data.find((c) => c.category === "administrador");
  if (admin?.enabled) return true;

  const cat = data.find((c) => c.category === category);
  if (!cat?.enabled) return false;

  return actions.some((action) => cat.actions.find((a) => a.action === action)?.enabled);
}

export type PermissionGateStatus = "loading" | "allowed" | "denied";

// Para gates de TELA INTEIRA (GeofencesPageGate, UsersPageGate,
// SettingsPageGate, VehiclesPageGate, AdminAccessGate, ReportsPageGate) —
// distingue "ainda não sei" de "sei que não tem". Bug real 2026-09-01:
// usePermission() (fail-closed, correto para botões/ações individuais)
// devolve false tanto enquanto useMyPermissions() ainda está carregando
// quanto quando realmente nega — um gate de tela inteira que só olha esse
// booleano mostra "Sem permissão" por 1-2s a cada carregamento (login,
// F5, troca de aba) até os dados chegarem, mesmo pra quem TEM a permissão
// ("quando eu acesso usuário ele aparece sem permissões e depois de
// segundos aparece"). Erro real de fetch é tratado como "denied" (estado
// terminal — nunca fica preso em loading pra sempre); enquanto ainda em
// voo, é "loading".
export function usePermissionGateStatus(
  category: PermissionCategoryKey,
  action?: string,
): PermissionGateStatus {
  const { data, isError } = useMyPermissions();
  if (!data) return isError ? "denied" : "loading";

  const admin = data.find((c) => c.category === "administrador");
  if (admin?.enabled) return "allowed";

  const cat = data.find((c) => c.category === category);
  if (!cat?.enabled) return "denied";
  if (!action) return "allowed";

  return cat.actions.find((a) => a.action === action)?.enabled ? "allowed" : "denied";
}

// Mesma ideia de usePermissionGateStatus, mas para gates que liberam a tela
// com QUALQUER uma das ações da lista (ex.: ReportsPageGate) — espelha
// useAnyPermission acima, só que distinguindo loading de negado.
export function useAnyPermissionGateStatus(
  category: PermissionCategoryKey,
  actions: string[],
): PermissionGateStatus {
  const { data, isError } = useMyPermissions();
  if (!data) return isError ? "denied" : "loading";

  const admin = data.find((c) => c.category === "administrador");
  if (admin?.enabled) return "allowed";

  const cat = data.find((c) => c.category === category);
  if (!cat?.enabled) return "denied";

  return actions.some((action) => cat.actions.find((a) => a.action === action)?.enabled)
    ? "allowed"
    : "denied";
}
